//@ts-check
class CSlider extends HTMLElement {
  constructor() {
    super()
    /** @type {string|null} */
    this.sliderId = this.dataset.id || null
    /** @type {HTMLElement|null} */
    this.containerBtn =
      document.querySelector(
        `[data-container-btn-slider="${this.sliderId}"]`
      ) || null
    /** @type {HTMLElement|null} */
    this.sliderInside = this.querySelector('.slider__inside') || null

    if (this.containerBtn) {
      /** @type {HTMLButtonElement|null} */
      this.btnPrev =
        this.containerBtn.querySelector('button:first-child') || null
      /** @type {HTMLButtonElement|null} */
      this.btnNext =
        this.containerBtn.querySelector('button:last-child') || null
    }

    this.handlePrev = this.handlePrev.bind(this)
    this.handleNext = this.handleNext.bind(this)
    this.updateButtonsState = this.updateButtonsState.bind(this)

    // For drag to scroll
    this.isDown = false
    this.startX = 0
    this.scrollLeftStart = 0
    this.isDragging = false
    this.handleMouseDown = this.handleMouseDown.bind(this)
    this.handleMouseLeave = this.handleMouseLeave.bind(this)
    this.handleMouseUp = this.handleMouseUp.bind(this)
    this.handleMouseMove = this.handleMouseMove.bind(this)
    this.handleClickCapture = this.handleClickCapture.bind(this)
  }

  connectedCallback() {
    if (!this.sliderInside) return

    // Écouteurs d'événements pour les boutons
    this.btnPrev?.addEventListener('click', this.handlePrev)
    this.btnNext?.addEventListener('click', this.handleNext)

    // Drag to scroll listeners
    this.addEventListener('mousedown', this.handleMouseDown)
    this.addEventListener('mouseleave', this.handleMouseLeave)
    this.addEventListener('mouseup', this.handleMouseUp)
    this.addEventListener('mousemove', this.handleMouseMove)
    this.addEventListener('click', this.handleClickCapture, true)

    // Observer les éléments aux extrémités du slider
    this.setupObservers()

    // État initial des boutons
    this.updateButtonsState()
  }

  disconnectedCallback() {
    this.btnPrev?.removeEventListener('click', this.handlePrev)
    this.btnNext?.removeEventListener('click', this.handleNext)

    // remove drag to scroll listeners
    this.removeEventListener('mousedown', this.handleMouseDown)
    this.removeEventListener('mouseleave', this.handleMouseLeave)
    this.removeEventListener('mouseup', this.handleMouseUp)
    this.removeEventListener('mousemove', this.handleMouseMove)
    this.removeEventListener('click', this.handleClickCapture, true)

    // Déconnecter les observers
    this.intersectionObserver?.disconnect()
    this.resizeObserver?.disconnect()
  }

  handleMouseDown(e) {
    if (e.button !== 0) return // Only for left click
    this.isDown = true
    this.isDragging = false
    this.classList.add('is-grabbing')
    this.startX = e.pageX - this.offsetLeft
    this.scrollLeftStart = this.scrollLeft
  }

  handleMouseLeave() {
    this.isDown = false
    this.classList.remove('is-grabbing')
  }

  handleMouseUp() {
    this.isDown = false
    this.classList.remove('is-grabbing')
  }

  handleMouseMove(e) {
    if (!this.isDown) return
    e.preventDefault()
    const x = e.pageX - this.offsetLeft
    const walk = x - this.startX
    if (Math.abs(walk) > 5) {
      this.isDragging = true
    }
    this.scrollLeft = this.scrollLeftStart - walk
  }

  handleClickCapture(e) {
    if (this.isDragging) {
      e.stopPropagation()
      e.preventDefault()
    }
  }

  setupObservers() {
    if (!this.sliderInside) return
    const slides = [...this.sliderInside.querySelectorAll('[data-slide]')]
    if (slides.length === 0) return

    const firstSlide = slides[0]
    const lastSlide = slides[slides.length - 1]

    // IntersectionObserver pour détecter les éléments visibles
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target === firstSlide) {
            this.isAtStart = entry.isIntersecting
          }

          if (entry.target === lastSlide) {
            this.isAtEnd = entry.isIntersecting
          }
        })
        this.updateButtonsState()
      },
      {
        root: this,
        threshold: 0.95, // 95% de l'élément doit être visible
      }
    )

    this.intersectionObserver.observe(firstSlide)
    this.intersectionObserver.observe(lastSlide)

    // ResizeObserver pour réagir aux changements de taille
    this.resizeObserver = new ResizeObserver(() => {
      this.updateButtonsState()
    })

    this.resizeObserver.observe(this)
  }

  handlePrev() {
    const scrollAmount = this.clientWidth * 0.8
    this.scrollBy({
      left: -scrollAmount,
      behavior: 'smooth',
    })
  }

  handleNext() {
    const scrollAmount = this.clientWidth * 0.8
    this.scrollBy({
      left: scrollAmount,
      behavior: 'smooth',
    })
  }

  updateButtonsState() {
    if (!this.btnPrev || !this.btnNext) return

    if (this.isAtStart) {
      this.btnPrev.disabled = true
      this.btnPrev.setAttribute('aria-disabled', 'true')
    } else {
      this.btnPrev.disabled = false
      this.btnPrev.setAttribute('aria-disabled', 'false')
    }

    if (this.isAtEnd) {
      this.btnNext.disabled = true
      this.btnNext.setAttribute('aria-disabled', 'true')
    } else {
      this.btnNext.disabled = false
      this.btnNext.setAttribute('aria-disabled', 'false')
    }
    if (this.btnNext.disabled && this.btnPrev.disabled) {
      this.btnPrev.classList.add('is-hide')
      this.btnNext.classList.add('is-hide')
    } else {
      this.btnPrev.classList.remove('is-hide')
      this.btnNext.classList.remove('is-hide')
    }
  }
}
if (!customElements.get('c-slider')) {
  customElements.define('c-slider', CSlider)
}
